/* eslint-disable eqeqeq */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import type {
  ISizerCabinetDimensions,
  ISizerDeviation,
  ISizerOptimalSize,
} from 'app/services/sizerApi';
import type { SizerMatrixRequestState } from '../useSizerSettings';
import { SizerMatrixEngine } from '../matrixEngine';
import {
  writeSizerAirflow,
  writeSizerPopupSelection,
  type SizerPopupSelection,
} from '../sizerStorage';

/**
 * Phase 4c/4d — `tmpl/matrix.tmpl` (39 lines) plus the interaction handlers the
 * matrix contributes to the parent view's `events` hash
 * (`view/main.js:47-49` → `activeColumns` ×2, `openSelection`).
 *
 * DIVISION OF LABOUR: this component renders the SCAFFOLD — the axis captions,
 * the two frozen header tables and the px-sized scroll canvas — and hands the
 * four DOM nodes `MatrixView` needs to `SizerMatrixEngine`, which owns the
 * children of `#main-table` imperatively (see that file's docblock for why).
 *
 * ★ THE TABLES NEED EXPLICIT `<tbody><tr>`. `matrix.tmpl:18-25` writes bare
 * `<th>` children straight inside `<table>`; the HTML PARSER silently wraps them
 * in `tbody > tr`, which is what the live DOM shows (OBSERVED:
 * `<table id="main-table-column-headers" …><tbody><tr><th></th><th id="w12.00">…`).
 * React builds DOM through `createElement`, which does NO such fixup, so the
 * wrappers are written out here to land on the same tree.
 */

export interface SizerMatrixHandle {
  /**
   * `changeAirflow`'s matrix half — main.js:648-650, as ONE call because the
   * ORDER relative to `changeInput` is observable and React makes it awkward:
   *
   *   1. `changeInput`  (main.js:38, bound FIRST) → `updateMatrixData()` →
   *      `matrixView.update_data(post_data)`, which SYNCHRONOUSLY installs the
   *      new settings, wipes the page cache and windows around the CURRENT
   *      (pre-reset) scroll position.
   *   2. `changeAirflow` (main.js:40) → `scrollTop(0)`, `scrollLeft(0)`, then
   *      `updateOptimals(+airflow)` — which therefore POSTs the NEW settings
   *      (OBSERVED: the optimal body of a 20 000 Size carries `airflow: 20000`).
   *
   * In React step 1 goes through `useSizerSettings` state (`matrixRequest`), so
   * it lands one effect LATER than the DOM event. `sizeTo` therefore QUEUES the
   * airflow and the queue is drained by the revision effect immediately after
   * `updateData` — restoring the legacy order exactly. Step 2 always follows a
   * step 1: `changeAirflow` bails on an empty airflow (`:644`) and `changeInput`
   * bails on an empty value (`:380`), so leg 2 running implies leg 1 ran.
   */
  sizeTo: (airflow: number) => void;
}

export interface SizerMatrixProps {
  cabinetDimensions: ISizerCabinetDimensions;
  /** `[28, 28]` for construction class 1; `[14, 28]` for class 5. */
  pagesCount: [number, number];
  /** `[10, 10]`. */
  pageSize: [number, number];
  deviation: ISizerDeviation | null;
  optimalSizes: ISizerOptimalSize[];
  /** the 4b seam — `{ payload, revision }`; a NEW revision means re-POST. */
  matrixRequest: SizerMatrixRequestState | null;
  /**
   * 4e — `openSelection`'s tail (main.js:635-636): hand the clicked cell's
   * `selectionData` to the selection popup. Its ABSENCE is legacy's `:598`
   * guard (`if (!window.header_view.sizer_popup) return`): no popup, no-op.
   */
  onSelectCell?: (selection: SizerPopupSelection) => void;
}

const SizerMatrix = forwardRef<SizerMatrixHandle, SizerMatrixProps>(function SizerMatrix(
  {
    cabinetDimensions,
    pagesCount,
    pageSize,
    deviation,
    optimalSizes,
    matrixRequest,
    onSelectCell,
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const columnHeadersRef = useRef<HTMLTableElement | null>(null);
  const rowHeadersRef = useRef<HTMLTableElement | null>(null);
  const baseRef = useRef<HTMLDivElement | null>(null);
  const preloaderRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef<SizerMatrixEngine | null>(null);

  // `self.currentlyActiveBlock / Column / Row` (main.js:575-593). Legacy writes
  // these to `window` — it uses a bare `self` with no `const self = this`, so
  // the assignments land on the global object and it "works by accident".
  // Component state is the honest model.
  const activeBlockRef = useRef<HTMLElement | null>(null);
  const activeColumnRef = useRef<HTMLElement | null>(null);
  const activeRowRef = useRef<HTMLElement | null>(null);

  // Props are fixed for the component's lifetime: the parent KEYS this
  // component on the construction class, so a Product Class change unmounts and
  // rebuilds it — which is precisely what legacy `render(true)` does by
  // constructing a brand-new `MatrixView` (main.js:327-331).
  const bootRef = useRef({ cabinetDimensions, pagesCount, pageSize, deviation, optimalSizes });
  bootRef.current = { cabinetDimensions, pagesCount, pageSize, deviation, optimalSizes };

  useLayoutEffect(() => {
    const boot = bootRef.current;
    if (!containerRef.current || !baseRef.current || !columnHeadersRef.current) return undefined;
    if (!rowHeadersRef.current) return undefined;
    const engine = new SizerMatrixEngine({
      containerElement: containerRef.current,
      baseElement: baseRef.current,
      columnHeadersElement: columnHeadersRef.current,
      rowHeadersElement: rowHeadersRef.current,
      preloaderElement: preloaderRef.current,
      cabinetDimensions: boot.cabinetDimensions,
      sizerPagesCount: boot.pagesCount,
      sizerPageSize: boot.pageSize,
      deviation: boot.deviation,
      optimalWidthAndHeight: boot.optimalSizes,
    });
    engineRef.current = engine;
    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, []);

  /**
   * ★ THE 4b→4c SEAM. `revision` — not the body — is the trigger: legacy's
   * `updateMatrixData` calls `matrixView.update_data(post_data)` unconditionally
   * (main.js:563) and `update_data` wipes `self.pages` wholesale (matrix.js:96),
   * so an identical body still means "throw everything away and re-POST".
   */
  const appliedRevisionRef = useRef<number | null>(null);
  /** the airflow queued by `sizeTo`, drained right after the next `updateData`. */
  const pendingSizeRef = useRef<number | null>(null);
  useEffect(() => {
    if (!matrixRequest) return;
    if (appliedRevisionRef.current === matrixRequest.revision) return;
    /**
     * ★ A freshly REMOUNTED matrix must not replay the PREVIOUS construction
     * class's request. Legacy's new `MatrixView` is built with no
     * `self.settings` at all (matrix.js:30-89 fetches nothing) and paints
     * nothing until the `updateMatrixData()` that trails `render(true)`
     * (main.js:416-422) — so a Product Class change costs exactly ONE
     * `update_data`. React remounts this component on the class key while the
     * old revision is still the current prop, and without this guard it replays
     * that revision against the new grid: MEASURED 8 POSTs (4 for the old
     * class, 4 for the new) where legacy issues 4. A payload is only renderable
     * against the grid it was built for, so matching the class IS the
     * condition. Deliberately NOT recorded in `appliedRevisionRef` — the
     * matching revision must still be free to apply.
     */
    if (matrixRequest.payload.construction_class_id != cabinetDimensions.construction_class_id) {
      return;
    }
    appliedRevisionRef.current = matrixRequest.revision;
    void engineRef.current?.updateData(matrixRequest.payload);
    // main.js:648-650 — the rest of `changeAirflow`, in legacy order and now
    // that `update_data` has installed the new settings.
    const pending = pendingSizeRef.current;
    if (pending == null) return;
    pendingSizeRef.current = null;
    const container = containerRef.current;
    if (container) {
      container.scrollTop = 0; // :648
      container.scrollLeft = 0; // :649
    }
    engineRef.current?.updateOptimals(pending); // :650
  }, [matrixRequest, cabinetDimensions]);

  useImperativeHandle(
    ref,
    () => ({
      sizeTo: (airflow: number) => {
        pendingSizeRef.current = airflow;
      },
    }),
    []
  );

  // -------------------------------------------------------------------------
  // activeColumns — main.js:573-595
  // -------------------------------------------------------------------------

  /**
   * Hover highlight: the block plus its column `<th id="w…">` and row
   * `<th id="h…">`, keyed off the block id `w{width}_h{height}` split on `_`.
   *
   * Quirks kept: the handler is bound to BOTH mouseenter and mouseleave and
   * branches on `e.type` (`:582`); the previous trio is always cleared first, so
   * only one cell can be highlighted at a time; and `document.getElementById`
   * is dereferenced with NO null guard (`:591-593`) — the ids always exist.
   * (`let currentId = e.currentTarget.id` at `:574` is assigned and never used —
   * not ported.)
   */
  const activeColumns = useCallback((block: HTMLElement, isLeave: boolean) => {
    if (activeBlockRef.current) activeBlockRef.current.classList.remove('selected');
    if (activeColumnRef.current) activeColumnRef.current.classList.remove('selected');
    if (activeRowRef.current) activeRowRef.current.classList.remove('selected');

    if (isLeave) {
      // ⚠ Verbatim: legacy does NOT null the three handles on mouseleave, it
      // only strips the classes. Harmless — the next enter overwrites them.
      return;
    }
    activeBlockRef.current = block;
    activeColumnRef.current = document.getElementById(block.id.split('_')[0]);
    activeRowRef.current = document.getElementById(block.id.split('_')[1]);

    activeBlockRef.current.classList.add('selected');
    activeColumnRef.current?.classList.add('selected');
    activeRowRef.current?.classList.add('selected');
  }, []);

  /**
   * jQuery's `mouseenter`/`mouseleave` delegation, reproduced on the native
   * `mouseover`/`mouseout` pair: a crossing INSIDE the same `.block` (between
   * its `.box-vertical-align` rows) is not an enter/leave of the block.
   * Delegated from the container so the imperatively-appended blocks are covered
   * without per-node listeners — exactly the Backbone arrangement.
   */
  const crossesBlockBoundary = (
    event: React.MouseEvent<HTMLDivElement>
  ): HTMLElement | null => {
    const target = event.target as Element | null;
    const block = target?.closest?.('.block') as HTMLElement | null;
    if (!block) return null;
    const related = event.relatedTarget;
    if (related instanceof Node && block.contains(related)) return null;
    return block;
  };

  const handleMouseOver = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const block = crossesBlockBoundary(event);
      if (block) activeColumns(block, false);
    },
    [activeColumns]
  );

  const handleMouseOut = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const block = crossesBlockBoundary(event);
      if (block) activeColumns(block, true);
    },
    [activeColumns]
  );

  // -------------------------------------------------------------------------
  // openSelection — main.js:596-637
  // -------------------------------------------------------------------------

  /**
   * ★★ `openSelection` (main.js:596-637) — the cell click that opens the AHU
   * selection popup, i.e. the entry point to the ONLY write path in the whole
   * sizer feature. 4e landed the other end: `../selection/SizerSelectionPopup`.
   *
   * The legacy guard at `:598` was `if (!window.header_view.sizer_popup) return`
   * — a test that the shared Backbone header had finished building the popup.
   * The React port has no `window.header_view`; the equivalent condition is
   * "the page has mounted a popup controller", which is what `onSelectCell`
   * being present means. When it is absent the handler is inert, exactly as
   * legacy is before the header resolves.
   *
   * Quirks reproduced, in legacy's order:
   *  - `:600` strips `.selected` from the clicked block (the hover highlight),
   *    WITHOUT touching the column/row `<th>`s or the three `currentlyActive*`
   *    handles — so the header cells stay highlighted until the next hover.
   *  - `:602-604` is a `[1,2,3,4].map(e => e*2)` whose result is discarded. Not
   *    ported.
   *  - `:617-618` writes `casing_insulation_id` TWICE — a duplicate object key,
   *    same expression both times; the second silently wins. One write here.
   *  - `casing_insulation_id` is coerced with unary `+` (a NUMBER) but
   *    `construction_class_id` is not (a STRING) — asymmetric, and both are read
   *    back that way by `apply()` (`+self.data.construction_class_id || 1`).
   *  - `:623` `airflow: $(el).value` reads `.value` off a jQuery OBJECT, which
   *    has no such property, so the key is `undefined` and `JSON.stringify`
   *    drops it. The absence is reproduced (never set the key) — it is what
   *    makes `apply()` fall back to `d-sizer-airflow`.
   *  - `:629-631` `.replace(',', '')` strips only the FIRST comma.
   *  - the two selects are read from the DOM, exactly as legacy does, and from
   *    the SETTINGS popup — which is always mounted (`SizerSettingsPopup` is
   *    rendered unconditionally into `#settings`; `active` only toggles a class),
   *    so the read is always live. `self.$('select[name=casing_insulation]')` is
   *    scoped to `#ksdm-content` while `$('select[name=construction_class_id]')`
   *    is global; both resolve to the same node, so one query each.
   */
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as Element | null;
      const block = target?.closest?.('.block') as HTMLElement | null;
      if (!block) return;
      if (!onSelectCell) return; // main.js:598 — no popup, no-op
      block.classList.remove('selected'); // :600

      const { page_x: pageX, page_y: pageY, x_on_page: xOnPage, y_on_page: yOnPage } = block.dataset;
      const pages = engineRef.current?.pages;
      if (!pages || pageX == null || pageY == null || xOnPage == null || yOnPage == null) return;
      // :612-616 — index straight back into the page cache. Legacy dereferences
      // with no guards at all; the ids come from the very node the engine wrote,
      // so a miss is impossible in practice.
      const row = pages[Number(pageY)]?.[Number(pageX)]?.[Number(yOnPage)];
      const cell = row?.elements?.[Number(xOnPage)];
      if (!row || !cell) return;

      const casingInsulation = document.querySelector<HTMLSelectElement>(
        'select[name="casing_insulation"]'
      );
      const constructionClass = document.querySelector<HTMLSelectElement>(
        'select[name="construction_class_id"]'
      );

      const selectionData: SizerPopupSelection = {
        ...cell, // :613-615
        current_height: row.current_height, // :615-616
        casing_insulation_id: Number(casingInsulation?.value), // :617-618
        construction_class_id: constructionClass?.value, // :619 — STRING
        // :623 `airflow` — always `undefined`, so the key is simply absent.
      };

      const airflowInput = document.querySelector<HTMLInputElement>('input[name="airflow"]');
      const airflow = (airflowInput?.value ?? '').replace(',', ''); // :628-631
      writeSizerAirflow(airflow); // :632-633
      writeSizerPopupSelection(selectionData); // :634
      onSelectCell(selectionData); // :635-636 — `update()` then `open_popup()`
    },
    [onSelectCell]
  );

  // -------------------------------------------------------------------------

  const widthNames = cabinetDimensions.width_value_names ?? [];
  const heightNames = cabinetDimensions.height_value_names ?? [];

  return (
    <div className="col-xs-12 scrollbar-table mt-0">
      <div className="row scrollbar-table__image">
        <img src="/static/sizer/images/ch_cw.png" alt="" />
      </div>
      <div className="row">
        <div className="scrollbar-table__width">CABINET WIDTH (OD)</div>
      </div>
      <div className="row" style={{ width: '30px', float: 'left' }}>
        <div className="scrollbar-table__height">CABINET HEIGHT (OD)</div>
      </div>
      <div
        className="row scroll-content"
        id="main-table-container"
        ref={containerRef}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        onClick={handleClick}
      >
        {/* z-index 1 while optimals compute (matrix.js:105), back to 0 when a
            scroll-driven render reaches its last row (matrix.js:443). */}
        <div id="cad_simple_preloader" className="" ref={preloaderRef} />
        <div
          id="main-table-container-scrollable"
          ref={scrollableRef}
          style={{
            position: 'relative',
            // matrix.js:241-248 — the canvas is the WHOLE grid in pixels
            // (26 592 × 19 390 for construction class 1). Derived from the
            // dimensions payload, never hardcoded: class 5's `width_increment`
            // of 2 halves `width_values` to 139 and the canvas with it.
            width: `${Math.round(96 * cabinetDimensions.width_values.length)}px`,
            height: `${Math.round(70 * cabinetDimensions.height_values.length)}px`,
          }}
        >
          <table
            id="main-table-column-headers"
            className="fixed-side main-table-column-headers"
            ref={columnHeadersRef}
          >
            <tbody>
              <tr>
                {/* matrix.tmpl:19 — the empty corner cell above the row headers. */}
                <th />
                {widthNames.map((name) => (
                  <th id={`w${name}`} key={name}>
                    {name}
                  </th>
                ))}
              </tr>
            </tbody>
          </table>
          <table
            id="main-table-row-headers"
            className="main-table-row-headers fixed-side"
            style={{ float: 'left' }}
            ref={rowHeadersRef}
          >
            <tbody>
              {heightNames.map((name) => (
                <tr key={name}>
                  <th id={`h${name}`}>{name}</th>
                </tr>
              ))}
            </tbody>
          </table>
          {/* The block host. Legacy re-applies `position:absolute` inline on
              every single block render (matrix.js:508-510, a
              set → removeAttr('style') → set dance whose only lasting effect is
              this one declaration); set once here instead. */}
          <div id="main-table" className="main-table" style={{ position: 'absolute' }} ref={baseRef} />
        </div>
      </div>
    </div>
  );
});

export default SizerMatrix;
